import React, { useState, useEffect } from 'react'
import compose from 'recompose/compose'
import { withI18n } from '../../hocs'
import { ModalStyled } from './styled'
import ActionDialog from '../action-dialog'
import { InputField, CustomAlert, RichTextEditor, ColorPicker } from '../../atoms'
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { FormControlLabel, Switch } from '@material-ui/core';
import { Radio } from '@material-ui/core';
import { AlertTriangle } from 'lucide-react';
import { tokens } from '../../../assets/tokens';
import { useAppContext } from '../../../context/appContext';
import { ALERT_LEVEL } from '../../../config/constants';

const ModalEditState = ({ open, handleClose, i18n, onSubmitEditState, statusItem, phaseId, phasesData, color, description }) => {
  const { createAlert } = useAppContext();
  const [state, setState] = useState(statusItem.name);
  const [descriptionStatus, setDescriptionStatus] = useState(description);
  const [changedPhase, setChangedPhase] = useState(phaseId);
  const [alert, setAlert] = useState({ message: '', severity: '' });
  const [showAlert, setShowAlert] = useState(false);
  const [originalState, setOriginalState] = useState(statusItem);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSwatchColor, setSelectedSwatchColor] = useState(color);
  const defaultColor = '#1976d2';
  const [checkedSwitch, setCheckedSwitch] = useState(false);
  const [smsChecked, setSmsChecked] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [whatsappChecked, setWhatsappChecked] = useState(false);

  useEffect(() => {
    if (open) {
      setIsOpen(true);
      setOriginalState(statusItem);
      setState(statusItem.name);
      setSelectedSwatchColor(color);
      setDescriptionStatus(description);
      setChangedPhase(phaseId);

      const notificationMethods = statusItem.notification_methods || [];
      setCheckedSwitch(notificationMethods.length > 0);
      setSmsChecked(notificationMethods.includes('sms'));
      setEmailChecked(notificationMethods.includes('email'));
      setWhatsappChecked(notificationMethods.includes('whatsapp'));
    }
  }, [open, statusItem.name, color, description, phaseId]);


  useEffect(() => {
    if (alert.message) {
      setShowAlert(true);

      const timer = setTimeout(() => {
        setShowAlert(false);
        setAlert({ message: '', severity: '' });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [alert]);

  const oncChangeState = e => {
    setState(e.target.value)
  }

  const options = (phasesData?.data ?? []);

  const onClickSubmit = async e => {
    e.preventDefault()

    if (!statusItem) {
      return;
    }

    if (state.trim() === '') {
      setAlert({ message: i18n.phases_states.alertStateNameRequired, severity: 'info' });
      setShowAlert(true);
      return;
    }

    const cleanedDescription = descriptionStatus.replace(/<[^>]*>/g, '');

    if (cleanedDescription.trim() === '') {
      setAlert({ message: i18n.phases_states.alertMessageFeedback, severity: 'info' });
      setShowAlert(true);
      return;
    }

    const selectedNotificationMethods = [];
    if (smsChecked) {
      selectedNotificationMethods.push('sms');
    }
    if (emailChecked) {
      selectedNotificationMethods.push('email');
    }
    if (whatsappChecked) {
      selectedNotificationMethods.push('whatsapp');
    }

    const updatedState = { id: statusItem.id, name: state, phaseId: phaseId, color: selectedSwatchColor, description: descriptionStatus, changedPhase: changedPhase, notification_methods: selectedNotificationMethods };

    // Spinner en Guardar mientras dura la petición; se cierra y sale el toast solo si va bien
    try {
      await onSubmitEditState(updatedState)
      handleClose();
      createAlert(i18n.phases_states.updatedStatus, ALERT_LEVEL.SUCCESS);
    } catch (error) {
      setAlert({ message: i18n.phases_states.errorUpdatedStatus, severity: 'error' });
    }

  }
  const resetModalValues = () => {
    if (isOpen) {
      setIsOpen(false);
      setState(originalState ? originalState.name : '');
      setSelectedSwatchColor(originalState ? originalState.color : color);
      setDescriptionStatus(originalState ? originalState.description : '');
      setChangedPhase(originalState ? originalState.changedPhase : phaseId);
    }
  };

  const handleColorChange = (newColor) => {
    setSelectedSwatchColor(newColor.hex);
  };

  const handleQuillChange = (value) => {
    setDescriptionStatus(value);
  }

  const handlePhaseChange = (e, value) => {
    if (value) {
      setChangedPhase(value.id);
    }
  }

  const handleChangeSwitch = (e) => {
    setCheckedSwitch(e.target.checked);
    if (!e.target.checked) {
      setSmsChecked(false);
      setEmailChecked(false);
      setWhatsappChecked(false);
    }
  };

  const handleRadioChange = (selectedOption) => {
    setSmsChecked(selectedOption === 'sms');
    setEmailChecked(selectedOption === 'email');
    setWhatsappChecked(selectedOption === 'whatsapp');
  };

  const onCloseModal = () => {
    handleClose();
    resetModalValues();
  };

  return (
    <ActionDialog
      open={open}
      title={i18n.phases_states.modalEditStateTitle}
      onClose={onCloseModal}
      onConfirm={onClickSubmit}
      cancelLabel={i18n.phases_states.buttonCancel}
      confirmLabel={i18n.phases_states.buttonSave}
      maxWidth="sm"
      fullWidth
      disableBackdropClose
    >
      <ModalStyled>
        <form>
          <div className="formField">
            <label>{i18n.phases_states.labelState}</label>
            <InputField placeholder={i18n.phases_states.placeholderState} value={state} onChange={oncChangeState} />
          </div>
          <div className="formField">
            <label>{i18n.phases_states.labelPhase}</label>
            <Autocomplete
              options={options}
              onChange={handlePhaseChange}
              getOptionLabel={(option) => option.name}
              defaultValue={(phasesData?.data ?? []).find((phase) => phase.id === phaseId) || {}}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
              fullWidth
            />
          </div>
          <div className="formField">
            <label>{i18n.phases_states.labelColor}</label>
            <ColorPicker
              defaultColor={defaultColor}
              onChange={handleColorChange}
              color={color}
            />
          </div>
          <div className="formField">
            <label>{i18n.phases_states.feedbackMessage}</label>
            <RichTextEditor
              onChange={handleQuillChange}
              placeholder={i18n.phases_states.placeholderDescriptionStatus}
              value={descriptionStatus}
            />
          </div>

          <div className="notificationField">
            <div className="notificationSwitch">
              <Switch
                checked={checkedSwitch}
                onChange={handleChangeSwitch}
                name="checkedA"
                inputProps={{ 'aria-label': 'switchA' }}
              />
              <span>{i18n.phases_states.questionNotified}</span>
            </div>
            {checkedSwitch && (
              <div className="notificationMethods">
                <span>{i18n.phases_states.selectNotificationMethods}</span>
                <div className="notificationInfo">
                  <AlertTriangle size={18} color={tokens.color.warning.main} />
                  <span>{i18n.phases_states.infoSelectNotificationMethods}</span>
                </div>
                <div className="notificationRadios">
                  <FormControlLabel
                    className='optional'
                    control={<Radio checked={smsChecked} />}
                    label={i18n.phases_states.smsMethod}
                    onChange={() => handleRadioChange('sms')}
                  />
                  <FormControlLabel
                    className='optional'
                    control={<Radio checked={emailChecked} />}
                    label={i18n.phases_states.emailMethod}
                    onChange={() => handleRadioChange('email')}
                  />
                  <FormControlLabel
                    className='optional'
                    control={<Radio checked={whatsappChecked} />}
                    label={i18n.phases_states.whatsapMethod}
                    onChange={() => handleRadioChange('whatsapp')}
                  />
                </div>
              </div>
            )}
          </div>

          {showAlert && <CustomAlert severity={alert.severity} message={alert.message} />}
        </form>
      </ModalStyled>
    </ActionDialog>
  )
}

const enharce = compose(withI18n)

export default enharce(ModalEditState)